import axiosClient from '../utils/apiClient';
import { isApiError, validateAndDecodeAuthToken } from '../utils/helper';
import { getEnvironmentVariable } from '../helper/config';
import { EnvVariables } from '../utils/helper.type';
import apiClient from '../utils/apiClient';
import { AxiosResponse } from 'axios';
import {
  AcceptTermsResponse,
  AuthTokenResponse,
  ForgotPasswordResponse,
  LogoutResponse,
  NewPasswordResponse,
  ResetPasswordResponse,
  SendUserVerificationCodeResponse,
  VerifyOtpResponse,
  VerifyUserVerificationCodeResponse,
} from './service.type';
import {
  AcceptTermsRequest,
  LoginResponse,
  RefreshAccessTokenResponse,
  UpdateUserResponse,
} from '@inplay/models';
import { EncryptionService } from '@inplay/services/dist/src/encryptionService';

export class AuthService {
  private baseApi: string;

  constructor() {
    const baseURL: string | undefined = axiosClient.defaults.baseURL;

    if (!baseURL) {
      throw new Error('baseURL is not defined in axiosClient.');
    }

    this.baseApi = baseURL;
  }

  public async logout(email: string): Promise<LogoutResponse> {
    try {
      console.log(`[AuthService][Logout][ENTRY]`, { email });

      const logoutApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_LOGOUT_RESOURCE,
      );
      const logoutApiEndpoint: string = `${this.baseApi}${logoutApiResource}`;

      const logoutApiResponse: AxiosResponse<LogoutResponse> =
        await apiClient.post(logoutApiEndpoint, {
          email,
        });

      console.log(`[AuthService][logout]`, { logoutApiResponse });

      return logoutApiResponse.data;
    } catch (error: unknown) {
      console.error(`[AuthService][logout][ERROR]`, { error });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async getSalt(email: string): Promise<string> {
    try {
      console.log(`[AuthService][getSalt][ENTRY]`, { email });

      const saltApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_SALT_RESOURCE,
      );
      const saltApiEndpoint: string = `${
        this.baseApi
      }${saltApiResource}?email=${encodeURIComponent(email)}`;

      const saltApiResponse: AxiosResponse<{ salt: string }> =
        await apiClient.get(saltApiEndpoint);

      console.log(`[AuthService][getSalt][RESPONSE]`, { saltApiResponse });

      const salt: string = saltApiResponse.data.salt;

      if (!salt) throw new Error('Salt not returned');

      return salt;
    } catch (error: unknown) {
      console.error(`[AuthService][getSalt][ERROR]`, { error });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async login(email: string, password: string): Promise<LoginResponse> {
    try {
      console.log(`[AuthService][Login][ENTRY]`, { email, password });

      const salt: string = await this.getSalt(email);
      const encryptionService = new EncryptionService();
      const hashedPassword: string = encryptionService.hash(password, salt);

      const loginApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_LOGIN_RESOURCE,
      );
      const loginApiEndpoint: string = `${this.baseApi}${loginApiResource}`;

      const loginApiResponse: AxiosResponse<LoginResponse> =
        await apiClient.post(loginApiEndpoint, {
          email,
          password: hashedPassword,
        });

      console.log(`[AuthService][login]`, { loginApiResponse });

      return loginApiResponse.data;
    } catch (error: unknown) {
      console.error(`[AuthService][login][ERROR]`, { error });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    try {
      console.log(`[AuthService][Forgot][ENTRY]`, { email });

      const forgotApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_FORGOT_PASSWORD_RESOURCE,
      );
      const forgotApiEndpoint: string = `${this.baseApi}${forgotApiResource}`;

      const forgotApiResponse: ForgotPasswordResponse = await apiClient.post(
        forgotApiEndpoint,
        { email },
      );

      console.log(`[AuthService][Forgot]`, { forgotApiResponse });

      return forgotApiResponse;
    } catch (error: unknown) {
      console.error(`[AuthService][Forgot][ERROR]`, { error });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async resetPassword(
    email: string,
    newPassword: string,
    confirmationCode: string,
  ): Promise<ResetPasswordResponse> {
    try {
      console.log(`[AuthService][Reset][ENTRY]`, {
        email,
        newPassword,
        confirmationCode,
      });

      const salt: string = await this.getSalt(email);
      const encryptionService = new EncryptionService();
      const hashedPassword: string = encryptionService.hash(newPassword, salt);

      const resetApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_RESET_PASSWORD_RESOURCE,
      );
      const resetApiEndpoint: string = `${this.baseApi}${resetApiResource}`;

      const resetApiResponse: ForgotPasswordResponse = await apiClient.post(
        resetApiEndpoint,
        { email, newPassword: hashedPassword, confirmationCode },
      );

      console.log(`[AuthService][Reset]`, { resetApiResponse });

      return resetApiResponse;
    } catch (error: unknown) {
      console.error(`[AuthService][Reset][ERROR]`, { error });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async verifyOtp(
    email: string,
    sessionId: string,
    code: string,
  ): Promise<VerifyOtpResponse> {
    try {
      console.log(`[AuthService][VerifyOtp][ENTRY]`, {
        email,
        sessionId,
        code,
      });

      const verifyOtpApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_VERIFY_OTP_RESOURCE,
      );
      const verifyOtpApiEndpoint: string = `${this.baseApi}${verifyOtpApiResource}`;

      const verifyOtpApiResponse: VerifyOtpResponse = await apiClient.post(
        verifyOtpApiEndpoint,
        {
          email,
          sessionId,
          code,
        },
      );

      console.log(`[AuthService][VerifyOtp]`, { verifyOtpApiResponse });

      return verifyOtpApiResponse;
    } catch (error: unknown) {
      console.error(`[AuthService][VerifyOtp][ERROR]`, { error });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async newPassword(
    email: string,
    password: string,
    activationCode: string,
  ): Promise<VerifyOtpResponse> {
    try {
      console.log(`[AuthService][NewPassword][ENTRY]`, {
        email,
        password,
        activationCode,
      });

      const salt: string = await this.getSalt(email);
      const encryptionService = new EncryptionService();
      const hashedPassword: string = encryptionService.hash(password, salt);

      const newPasswordApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_NEW_PASSWORD,
      );
      const newPasswordApiEndpoint: string = `${this.baseApi}${newPasswordApiResource}`;

      const newPasswordApiResponse: NewPasswordResponse = await apiClient.post(
        newPasswordApiEndpoint,
        {
          email,
          password: hashedPassword,
          activationCode,
        },
      );

      console.log(`[AuthService][ResetPassword]`, { newPasswordApiResponse });

      return newPasswordApiResponse;
    } catch (error: unknown) {
      console.error(`[AuthService][ResetPassword][ERROR]`, { error });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async acceptTerms(
    acceptTermsRequest: AcceptTermsRequest,
  ): Promise<AcceptTermsResponse> {
    try {
      console.log(`[AuthService][AcceptTerm][ENTRY]`, {
        version: acceptTermsRequest.version,
      });

      const acceptTermsApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_ACCEPT_TERMS,
      );
      const acceptTermsApiEndpoint: string = `${this.baseApi}${acceptTermsApiResource}`;

      const acceptTermsApiResponse: AcceptTermsResponse = await apiClient.put(
        acceptTermsApiEndpoint,
        acceptTermsRequest,
      );

      console.log(`[AuthService][AcceptTerm]`, { acceptTermsApiResponse });

      return acceptTermsApiResponse;
    } catch (error: unknown) {
      console.error(`[AuthService][AcceptTerm][ERROR]`, { error });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async refreshToken(
    refreshToken: string,
  ): Promise<AxiosResponse<RefreshAccessTokenResponse>> {
    try {
      console.log(`[ENTRY][AuthService](refreshToken)...`);

      const refreshTokenApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_REFRESH_TOKEN_RESOURCE,
      );

      const refreshTokenApiEndpoint: string = `${this.baseApi}${refreshTokenApiResource}`;

      const refreshTokenResponse: AxiosResponse<RefreshAccessTokenResponse> =
        await apiClient.post(refreshTokenApiEndpoint, {
          refreshToken,
        });
      console.log(`[AuthService][refreshToken]`, {
        refreshTokenResponse,
      });

      console.log(`[EXIT][AuthService](refreshToken)...`);

      return refreshTokenResponse;
    } catch (error: unknown) {
      console.error(`[AuthService][refreshToken][ERROR]`, { error });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async changeProfilePassword(
    currentPassword: string,
    newPassword: string,
    idpToken: string,
    userEmail?: string,
  ): Promise<ForgotPasswordResponse> {
    try {
      const changeProfilePasswordApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_CHANGE_USER_PROFILE_RESOURCE,
      );

      // Get email either from provided userEmail or from current user's token
      let email: string;
      if (userEmail) {
        // Use provided email for universal staff user
        email = userEmail;
      } else {
        // Use current user's email from token
        const decodedData: AuthTokenResponse | null =
          validateAndDecodeAuthToken();
        email = decodedData?.email ?? '';
      }

      // Get salt using the appropriate email
      const salt: string = await this.getSalt(email);
      const encryptionService = new EncryptionService();
      const hashedCurrentPassword: string = encryptionService.hash(
        currentPassword,
        salt,
      );
      const hashedNewPassword: string = encryptionService.hash(
        newPassword,
        salt,
      );

      const changeProfilePasswordApiEndpoint: string = `${this.baseApi}${changeProfilePasswordApiResource}`;

      const changeProfileApiResponse: ForgotPasswordResponse =
        await apiClient.put(changeProfilePasswordApiEndpoint, {
          currentPassword: hashedCurrentPassword,
          newPassword: hashedNewPassword,
          idpToken,
        });

      return changeProfileApiResponse;
    } catch (error: unknown) {
      console.error(`[UserService][changeProfilePassword][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async deleteUsers(userIds: string[]): Promise<UpdateUserResponse> {
    try {
      console.log(`[ENTRY][UserService](deleteUsers)...`, {
        userIds,
      });

      const deleteUsersApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_DELETE_USER_RESOURCE,
      );

      const deleteUsersApiEndpoint: string = `${this.baseApi}${deleteUsersApiResource}`;

      const params: URLSearchParams = new URLSearchParams();
      userIds.forEach((id: string) => params.append('userIds', id));

      const urlWithParams: string = `${deleteUsersApiEndpoint}?${params.toString()}`;

      const deleteUsersApiResponse: UpdateUserResponse =
        await apiClient.delete(urlWithParams);

      console.log(`[EXIT][UserService](deleteUsers)`, {
        deleteUsersApiResponse,
      });
      return deleteUsersApiResponse;
    } catch (error: unknown) {
      console.error(`[UserService][deleteUsers][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async sendUserVerificationCode(
    email: string,
    userId: string,
  ): Promise<SendUserVerificationCodeResponse> {
    try {
      console.log(`[AuthService][SendUserVerificationCode][ENTRY]`, {
        email,
        userId,
      });
      const sendUserVerificationCodeResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_SEND_USER_VERIFICATION_CODE_RESOURCE,
      );
      const sendUserVerificationCodeEndpoint: string = `${this.baseApi}${sendUserVerificationCodeResource}`;

      const sendUserVerificationCodeApiResponse: SendUserVerificationCodeResponse =
        await apiClient.post(sendUserVerificationCodeEndpoint, {
          userId: userId,
          contactMethod: {
            type: 'email',
            email: email,
          },
        });

      console.log(`[AuthService][SendUserVerificationCode]`, {
        sendUserVerificationCodeApiResponse,
      });

      return sendUserVerificationCodeApiResponse;
    } catch (error: unknown) {
      console.error(`[AuthService][SendUserVerificationCode][ERROR]`, {
        error,
      });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async VerifyUserVerificationCode(
    code: string,
    session: string,
    userId: string,
  ): Promise<VerifyUserVerificationCodeResponse> {
    try {
      console.log(`[AuthService][VerifyUserVerificationCode][ENTRY]`, {
        code,
        session,
      });
      const verifyUserVerificationCodeResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_VERIFY_USER_VERIFICATION_CODE,
      );
      const verifyUserVerificationCodeEndpoint: string = `${this.baseApi}${verifyUserVerificationCodeResource}`;

      const verifyUserVerificationCodeApiResponse: VerifyUserVerificationCodeResponse =
        await apiClient.post(verifyUserVerificationCodeEndpoint, {
          code,
          session,
          userId,
        });

      console.log(`[AuthService][VerifyUserVerificationCode]`, {
        verifyUserVerificationCodeApiResponse,
      });

      return verifyUserVerificationCodeApiResponse;
    } catch (error: unknown) {
      console.error(`[AuthService][VerifyUserVerificationCode][ERROR]`, {
        error,
      });

      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }
}
